/**
 * Kiosk identity management.
 * Assigns a unique ID to each kiosk device for tracking and observability.
 */

const ID_KEY = "kiosk_id";
const NAME_KEY = "kiosk_name";
const LOCATION_KEY = "kiosk_location";

export interface KioskIdentity {
  kioskId: string;
  kioskName: string | null;
  kioskLocation: string | null;
}

/**
 * Generate a new unique kiosk ID.
 */
function generateId(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create the kiosk identity.
 * The ID is persisted in localStorage and survives refreshes.
 */
export function getKioskIdentity(): KioskIdentity {
  try {
    let kioskId = localStorage.getItem(ID_KEY);
    if (!kioskId) {
      kioskId = generateId();
      localStorage.setItem(ID_KEY, kioskId);
      console.log(`[kiosk-identity] Generated new kiosk ID: ${kioskId}`);
    }

    const kioskName = localStorage.getItem(NAME_KEY);
    const kioskLocation = localStorage.getItem(LOCATION_KEY);

    return { kioskId, kioskName, kioskLocation };
  } catch (error) {
    console.warn("[kiosk-identity] Failed to access localStorage:", error);
    // Return a temporary ID if localStorage is unavailable
    return {
      kioskId: generateId(),
      kioskName: null,
      kioskLocation: null,
    };
  }
}

/**
 * Get just the kiosk ID (for simpler use cases).
 */
export function getKioskId(): string {
  return getKioskIdentity().kioskId;
}

/**
 * Set a human-readable name for this kiosk.
 * Useful for identifying kiosks in admin dashboards.
 */
export function setKioskName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
    console.log(`[kiosk-identity] Set kiosk name: ${name}`);
  } catch (error) {
    console.warn("[kiosk-identity] Failed to set kiosk name:", error);
  }
}

/**
 * Set the location/site for this kiosk.
 */
export function setKioskLocation(location: string): void {
  try {
    localStorage.setItem(LOCATION_KEY, location);
    console.log(`[kiosk-identity] Set kiosk location: ${location}`);
  } catch (error) {
    console.warn("[kiosk-identity] Failed to set kiosk location:", error);
  }
}

/**
 * Get a short display ID (first 8 characters of UUID).
 */
export function getShortKioskId(): string {
  return getKioskId().substring(0, 8);
}

/**
 * Get a display name for the kiosk.
 * Returns the custom name if set, otherwise the short ID.
 */
export function getKioskDisplayName(): string {
  const identity = getKioskIdentity();
  return identity.kioskName || `Kiosk-${getShortKioskId()}`;
}
